// The practice table's checklist (qa-runs 1163): five steps, each ticked by the table's own events as they reach this
// browser (a move, a roll, a fight started, a turn passed, a fog reveal) or by hand. The ticks are this person's, kept
// in this browser per campaign; Reset unticks them.
export const PRACTICE_STEPS = [
  { id: 'move', label: 'Move a token', hint: 'Drag one of the characters to another square.' },
  { id: 'roll', label: 'Roll some dice', hint: 'Open the dice and roll anything: a d20 will do.' },
  { id: 'fight', label: 'Start a fight', hint: 'Start the turn order: the goblins and the characters roll initiative.' },
  { id: 'turn', label: 'Take a turn and pass it on', hint: 'End the turn so the next in the order acts.' },
  { id: 'fog', label: 'Reveal part of the map', hint: 'Use the fog tool to uncover where the goblins wait.' },
] as const;
export type PracticeStep = (typeof PRACTICE_STEPS)[number]['id'];

const key = (campaignId: string) => `vtt.practice.${campaignId}`;
const listeners = new Set<() => void>();
const memory = new Map<string, PracticeStep[]>();
/** Until when the table's changes are not the DM's steps: a Reset lays the scene out again under it. */
let quietUntil = 0;

export function ticked(campaignId: string): PracticeStep[] {
  if (memory.has(campaignId)) return memory.get(campaignId)!;
  let got: PracticeStep[] = [];
  try {
    const raw = JSON.parse(localStorage.getItem(key(campaignId)) ?? '[]') as unknown;
    if (Array.isArray(raw)) got = raw.filter((s): s is PracticeStep => PRACTICE_STEPS.some((p) => p.id === s));
  } catch { /* nothing kept */ }
  memory.set(campaignId, got);
  return got;
}

function save(campaignId: string, steps: PracticeStep[]): void {
  memory.set(campaignId, steps);
  try { localStorage.setItem(key(campaignId), JSON.stringify(steps)); } catch { /* kept for this visit only */ }
  for (const l of listeners) l();
}

export function tick(campaignId: string, step: PracticeStep, on = true): void {
  if (on && Date.now() < quietUntil) return;
  const now = ticked(campaignId);
  if (on === now.includes(step)) return;
  save(campaignId, on ? [...now, step] : now.filter((s) => s !== step));
}

/** Every step unticked, and the table's changes for the next moments not counted (the scene laid out again). */
export function untickAll(campaignId: string, quietMs = 0): void {
  quietUntil = Date.now() + quietMs;
  save(campaignId, []);
}

export function onTicks(l: () => void): () => void {
  listeners.add(l);
  return () => { listeners.delete(l); };
}
